import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { auth } from "../../firebase.js";
import "../../css/SpaceTableOfContents.css";

/**
 * Table of contents widget for spaces (BBD-58).
 *
 * Shown near the top of a space (below downloadable content). Renders the
 * space's page tree as returned by the backend TOC endpoints, which filter
 * out every document the requesting user is not allowed to view — so this
 * component never needs to reason about permissions itself.
 *
 * Variants:
 *  - personal:  <SpaceTableOfContents variant="personal" spaceName={name} authorUid={uid} />
 *  - workspace: <SpaceTableOfContents variant="workspace" workspaceId={id} spaceId={id} customDomain={domain} />
 *
 * Fails silently (renders nothing) when the space has no visible pages or
 * the request errors, so the document view is never disrupted.
 */

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

// Flatten the tree for counting/filter matching
const countNodes = (nodes) =>
    nodes.reduce((sum, n) => sum + 1 + countNodes(n.children || []), 0);

const filterTree = (nodes, query) => {
    if (!query) return nodes;
    const q = query.toLowerCase();
    return nodes
        .map((node) => {
            const children = filterTree(node.children || [], query);
            const selfMatch =
                (node.title || "").toLowerCase().includes(q) ||
                (node.description || "").toLowerCase().includes(q);
            if (!selfMatch && children.length === 0) return null;
            return { ...node, children };
        })
        .filter(Boolean);
};

function TocNode({ node, depth, expandedMap, onToggle, filterActive }) {
    const hasChildren = node.children && node.children.length > 0;
    // While filtering, always show matches expanded; otherwise honor the map
    const isExpanded = filterActive || expandedMap[node.key] !== false;

    return (
        <li className="SpaceToc__item">
            <div className="SpaceToc__row" style={{ "--toc-depth": depth }}>
                {hasChildren ? (
                    <button
                        type="button"
                        className={`SpaceToc__caret ${isExpanded ? "SpaceToc__caret--open" : ""}`}
                        onClick={() => onToggle(node.key)}
                        aria-label={isExpanded ? "Collapse section" : "Expand section"}
                        aria-expanded={isExpanded}
                    >
                        <i className="fas fa-chevron-right"></i>
                    </button>
                ) : (
                    <span className="SpaceToc__caret SpaceToc__caret--leaf">
                        <i className="far fa-file-lines"></i>
                    </span>
                )}

                <Link to={node.url} className="SpaceToc__link" title={node.description || node.title}>
                    <span className="SpaceToc__link-title">{node.title}</span>
                    {node.description && (
                        <span className="SpaceToc__link-description">{node.description}</span>
                    )}
                </Link>

                <span className="SpaceToc__meta">
                    {!node.completed && (
                        <span className="SpaceToc__badge SpaceToc__badge--draft" title="Not published yet">
                            Draft
                        </span>
                    )}
                    {hasChildren && (
                        <span
                            className="SpaceToc__badge SpaceToc__badge--count"
                            title={`${countNodes(node.children)} nested page${countNodes(node.children) === 1 ? "" : "s"}`}
                        >
                            <i className="fas fa-layer-group"></i>
                            {countNodes(node.children)}
                        </span>
                    )}
                </span>
            </div>

            {hasChildren && isExpanded && (
                <ol className="SpaceToc__list SpaceToc__list--nested">
                    {node.children.map((child) => (
                        <TocNode
                            key={child.key}
                            node={child}
                            depth={depth + 1}
                            expandedMap={expandedMap}
                            onToggle={onToggle}
                            filterActive={filterActive}
                        />
                    ))}
                </ol>
            )}
        </li>
    );
}

function SpaceTableOfContents({
    variant,
    spaceName,
    authorUid,
    workspaceId,
    spaceId,
    customDomain,
}) {
    const [tree, setTree] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [expandedMap, setExpandedMap] = useState({});
    const [filter, setFilter] = useState("");

    const storageKey =
        variant === "workspace"
            ? `bbd-toc-collapsed:ws:${workspaceId}:${spaceId}`
            : `bbd-toc-collapsed:${authorUid}:${spaceName}`;

    useEffect(() => {
        try {
            setCollapsed(localStorage.getItem(storageKey) === "1");
        } catch (e) { /* private mode etc. — default to expanded */ }
    }, [storageKey]);

    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            try {
                localStorage.setItem(storageKey, prev ? "0" : "1");
            } catch (e) { /* ignore */ }
            return !prev;
        });
    };

    // Normalize both backend shapes into { key, title, description, url, completed, children }
    const normalize = useCallback(
        (nodes) =>
            (nodes || []).map((node) => {
                const isWorkspace = variant === "workspace";
                const title = isWorkspace ? node.page_name : node.name;
                return {
                    key: isWorkspace ? `ws-${node.id}` : `p-${title}`,
                    title,
                    description: node.description && node.description !== "null" ? node.description : "",
                    completed: node.completed,
                    url: isWorkspace
                        ? `/workspace/${customDomain}/page/${node.id}`
                        : `/documentation/page/${encodeURIComponent(title)}/${authorUid}`,
                    children: normalize(node.children),
                };
            }),
        [variant, customDomain, authorUid]
    );

    useEffect(() => {
        let cancelled = false;

        const fetchToc = async () => {
            try {
                const user = auth.currentUser;
                const token = user ? await user.getIdToken() : null;

                let endpoint;
                let headers;

                if (variant === "workspace") {
                    if (!workspaceId || !spaceId || !token) return;
                    endpoint = `${API_BASE}/workspaces/${workspaceId}/spaces/${spaceId}/toc/`;
                    headers = {
                        "Auth-Token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "Requesting-User-Uid": user.uid,
                    };
                } else {
                    if (!spaceName || !authorUid) return;
                    endpoint = `${API_BASE}/documentation/toc/${encodeURIComponent(spaceName)}/${authorUid}/`;
                    headers = {
                        ...(user && { "Requesting-User-Uid": user.uid }),
                        ...(user && user.email && { "Requesting-User-Email": user.email }),
                        ...(token && { "Auth-Token": `Bearer ${token}` }),
                        ...(token && { "Authorization": `${token}` }),
                    };
                }

                const response = await fetch(endpoint, { method: "GET", headers });
                if (!response.ok) return; // fail silently — the TOC is a bonus, never a blocker

                const data = await response.json();
                if (!cancelled && data && Array.isArray(data.pages)) {
                    setTree(data.pages);
                }
            } catch (error) {
                console.error("Error fetching space table of contents:", error);
            }
        };

        fetchToc();
        return () => { cancelled = true; };
    }, [variant, spaceName, authorUid, workspaceId, spaceId]);

    const normalizedTree = useMemo(() => normalize(tree || []), [tree, normalize]);
    const totalPages = useMemo(() => countNodes(normalizedTree), [normalizedTree]);
    const visibleTree = useMemo(
        () => filterTree(normalizedTree, filter.trim()),
        [normalizedTree, filter]
    );

    const handleToggleNode = (key) => {
        setExpandedMap((prev) => ({ ...prev, [key]: prev[key] === false }));
    };

    if (!tree || totalPages === 0) return null;

    return (
        <nav className="SpaceToc" aria-label="Space table of contents">
            <button type="button" className="SpaceToc__header" onClick={toggleCollapsed} aria-expanded={!collapsed}>
                <span className="SpaceToc__header-icon">
                    <i className="fas fa-list-ul"></i>
                </span>
                <span className="SpaceToc__header-title">In this space</span>
                <span className="SpaceToc__header-count">
                    {totalPages} page{totalPages === 1 ? "" : "s"}
                </span>
                <span className={`SpaceToc__header-chevron ${collapsed ? "" : "SpaceToc__header-chevron--open"}`}>
                    <i className="fas fa-chevron-down"></i>
                </span>
            </button>

            {!collapsed && (
                <div className="SpaceToc__body">
                    {totalPages > 8 && (
                        <div className="SpaceToc__filter">
                            <i className="fas fa-magnifying-glass"></i>
                            <input
                                type="text"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter pages..."
                                aria-label="Filter pages"
                            />
                            {filter && (
                                <button
                                    type="button"
                                    className="SpaceToc__filter-clear"
                                    onClick={() => setFilter("")}
                                    aria-label="Clear filter"
                                >
                                    <i className="fas fa-xmark"></i>
                                </button>
                            )}
                        </div>
                    )}

                    {visibleTree.length > 0 ? (
                        <ol className="SpaceToc__list">
                            {visibleTree.map((node) => (
                                <TocNode
                                    key={node.key}
                                    node={node}
                                    depth={0}
                                    expandedMap={expandedMap}
                                    onToggle={handleToggleNode}
                                    filterActive={Boolean(filter.trim())}
                                />
                            ))}
                        </ol>
                    ) : (
                        <p className="SpaceToc__empty">No pages match "{filter}".</p>
                    )}
                </div>
            )}
        </nav>
    );
}

export default SpaceTableOfContents;
